import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AuthService } from '@insolution/auth-data-access';
import {
  Announcement,
  AnnouncementChangedEvent,
  CreateAnnouncementGQL,
  CreateAnnouncementInput,
  DeleteAnnouncementGQL,
  ListAnnouncementsGQL,
  OnAnnouncementChangedDocument,
  UpdateAnnouncementGQL,
  UpdateAnnouncementInput,
} from '@insolution/graphql-data-access';
import { Apollo } from 'apollo-angular';
import { ApolloCache } from '@apollo/client/core';
import { EMPTY, firstValueFrom, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AnnouncementService {
  private readonly listAnnouncementsGQL = inject(ListAnnouncementsGQL);
  private readonly createAnnouncementGQL = inject(CreateAnnouncementGQL);
  private readonly updateAnnouncementGQL = inject(UpdateAnnouncementGQL);
  private readonly deleteAnnouncementGQL = inject(DeleteAnnouncementGQL);
  private readonly apollo = inject(Apollo);
  private readonly authService = inject(AuthService);

  private setAnnouncementChangedSubscriptionIsCalled$ = new Subject<void>();

  constructor() {
    this.setAnnouncementChangedSubscription();
  }

  public watchAnnouncements$(): Observable<Announcement[]> {
    return this.listAnnouncementsGQL
      .watch(undefined, { fetchPolicy: 'cache-and-network' })
      .valueChanges.pipe(map((result) => result.data?.listAnnouncements ?? []));
  }

  public async getAnnouncements(): Promise<Announcement[]> {
    const result = await firstValueFrom(this.listAnnouncementsGQL.fetch(undefined, { fetchPolicy: 'network-only' }));

    return result.data?.listAnnouncements ?? [];
  }

  public async createAnnouncement(input: CreateAnnouncementInput): Promise<Announcement> {
    try {
      const result = await firstValueFrom(
        this.createAnnouncementGQL.mutate({ input }, { update: (cache) => this.evictAnnouncements(cache) })
      );

      return result.data!.createAnnouncement;
    } catch (error) {
      console.error('Error creating announcement:', error);
      return Promise.reject(error);
    }
  }

  public async updateAnnouncement(input: UpdateAnnouncementInput): Promise<Announcement> {
    try {
      const result = await firstValueFrom(
        this.updateAnnouncementGQL.mutate({ input }, { update: (cache) => this.evictAnnouncements(cache) })
      );

      return result.data!.updateAnnouncement;
    } catch (error) {
      console.error('Error updating announcement:', error);
      return Promise.reject(error);
    }
  }

  public async deleteAnnouncement(id: string): Promise<string> {
    try {
      const result = await firstValueFrom(
        this.deleteAnnouncementGQL.mutate({ id }, { update: (cache) => this.evictAnnouncements(cache) })
      );

      return result.data!.deleteAnnouncement.id;
    } catch (error) {
      console.error('Error deleting announcement:', error);
      return Promise.reject(error);
    }
  }

  private evictAnnouncements(cache: ApolloCache<unknown>): void {
    cache.modify({
      fields: {
        listAnnouncements(_, { DELETE }) {
          return DELETE;
        },
      },
    });
  }

  private announcementChanged$(): Observable<AnnouncementChangedEvent> {
    return this.apollo
      .subscribe({
        query: OnAnnouncementChangedDocument,
      })
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        filter((result) => !!(result.data as any)?.onAnnouncementChanged),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((result) => (result.data as any).onAnnouncementChanged)
      );
  }

  private setAnnouncementChangedSubscription(): void {
    this.setAnnouncementChangedSubscriptionIsCalled$.next();

    toObservable(this.authService.isLoggedIn)
      .pipe(
        takeUntil(this.setAnnouncementChangedSubscriptionIsCalled$),
        takeUntilDestroyed(),
        switchMap((isLoggedIn) => (isLoggedIn ? this.announcementChanged$() : EMPTY))
      )
      .subscribe(() => this.evictAnnouncements(this.apollo.client.cache));
  }
}
